<template>
  <div class="kyb-page">

    <div class="kyb-content">
      <div v-if="loading" class="loading-state">
        <BaseSpinner />
        <p>Loading KYB documents...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <i class="las la-exclamation-circle"></i>
        <p>{{ error }}</p>
        <button @click="loadKybDocuments" class="retry-btn">Retry</button>
      </div>

      <div v-else class="kyb-sections">
        <div class="kyb-section-header">
          <h2 class="kyb-section-title">KYB Documents</h2>
          <div class="status-legend">
            <span class="status-legend-item">
              <span class="status-badge status-completed"><i class="las la-check"></i></span>
              <span>Completed</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-pending"><i class="las la-clock"></i></span>
              <span>Pending</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-processing"><i class="las la-sync"></i></span>
              <span>Processing</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-error"><i class="las la-times"></i></span>
              <span>Error</span>
            </span>
          </div>
        </div>

        <!-- Emirates ID Section -->
        <div class="kyb-section">
          <div class="section-header">
            <h3 class="section-title">
              Identity Document
            </h3>
            <BaseButton
              v-if="!emiratesIdDoc"
              stylevariant="primary"
              :cus_style="{ width: 'auto', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }"
              @click="uploadDocument('emirates_id')"
              class="upload-section-btn"
            >
              <i class="las la-plus"></i>
              Upload
            </BaseButton>
          </div>
          <div v-if="emiratesIdDoc" class="document-card">
            <div class="document-card-content">
              <div class="document-icon-large">
                <i class="las la-id-card"></i>
              </div>
              <span class="document-name">{{ emiratesIdDoc.name }}</span>
              <span class="document-date">
                {{ formatDate(emiratesIdDoc.uploaded_at) }}
              </span>
              <span class="status-badge" :class="getStatusClass(emiratesIdDoc.status)">
                <i :class="getStatusIcon(emiratesIdDoc.status)"></i>
              </span>
              <button 
                v-if="emiratesIdDoc.status === 'completed' || emiratesIdDoc.status === 'success'"
                class="view-btn"
                @click.stop="viewDocument(emiratesIdDoc)"
                title="View Document"
              >
                <i class="las la-external-link-alt"></i>
              </button>
              <button 
                class="delete-btn"
                @click.stop="deleteDocument(emiratesIdDoc)"
                title="Delete Document"
              >
                <i class="las la-trash"></i>
              </button>
            </div>
          </div>
          <div v-else class="empty-document">
            <i class="las la-id-card"></i>
            <p>No Emirates ID uploaded</p>
          </div>
        </div>

        <!-- Trade License Section -->
        <div class="kyb-section">
          <div class="section-header">
            <h3 class="section-title">
              Trade License
            </h3>
            <BaseButton
              v-if="!tradeLicenseDoc"
              stylevariant="primary"
              :cus_style="{ width: 'auto', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }"
              @click="uploadDocument('trade_license')"
              class="upload-section-btn"
            >
              <i class="las la-plus"></i>
              Upload
            </BaseButton>
          </div>
          <div v-if="tradeLicenseDoc" class="document-card">
            <div class="document-card-content">
              <div class="document-icon-large">
                <i class="las la-file-contract"></i>
              </div>
              <span class="document-name">{{ tradeLicenseDoc.name }}</span>
              <span class="document-date">
                {{ formatDate(tradeLicenseDoc.uploaded_at) }}
              </span>
              <span class="status-badge" :class="getStatusClass(tradeLicenseDoc.status)">
                <i :class="getStatusIcon(tradeLicenseDoc.status)"></i>
              </span>
              <button 
                v-if="tradeLicenseDoc.status === 'completed' || tradeLicenseDoc.status === 'success'"
                class="view-btn"
                @click.stop="viewDocument(tradeLicenseDoc)"
                title="View Document"
              >
                <i class="las la-external-link-alt"></i>
              </button>
              <button 
                class="delete-btn"
                @click.stop="deleteDocument(tradeLicenseDoc)"
                title="Delete Document"
              >
                <i class="las la-trash"></i>
              </button>
            </div>
          </div>
          <div v-else class="empty-document">
            <i class="las la-file-contract"></i>
            <p>No Trade License uploaded</p>
          </div>
        </div>

        <!-- Credit Report Section -->
        <div class="kyb-section">
          <div class="section-header">
            <h3 class="section-title">
              Credit Report
            </h3>
            <BaseButton
              v-if="!creditReportDoc"
              stylevariant="primary"
              :cus_style="{ width: 'auto', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }"
              @click="uploadDocument('credit_report')"
              class="upload-section-btn"
            >
              <i class="las la-plus"></i>
              Upload
            </BaseButton>
          </div>
          <div v-if="creditReportDoc" class="document-card">
            <div class="document-card-content">
              <div class="document-icon-large">
                <i class="las la-chart-line"></i>
              </div>
              <span class="document-name">{{ creditReportDoc.name }}</span>
              <span class="document-date">
                {{ formatDate(creditReportDoc.uploaded_at) }}
              </span>
              <span class="status-badge" :class="getStatusClass(creditReportDoc.status)">
                <i :class="getStatusIcon(creditReportDoc.status)"></i>
              </span>
              <button 
                v-if="creditReportDoc.status === 'completed' || creditReportDoc.status === 'success'"
                class="view-btn"
                @click.stop="viewDocument(creditReportDoc)"
                title="View Document"
              >
                <i class="las la-external-link-alt"></i>
              </button>
              <button 
                class="delete-btn"
                @click.stop="deleteDocument(creditReportDoc)"
                title="Delete Document"
              >
                <i class="las la-trash"></i>
              </button>
            </div>
          </div>
          <div v-else class="empty-document">
            <i class="las la-chart-line"></i>
            <p>No Credit Report uploaded</p>
          </div>
        </div>

        <!-- Supporting Documents Section -->
        <div class="kyb-section">
          <div class="section-header">
            <h3 class="section-title">
              Supporting Documents
            </h3>
          </div>
          
          <!-- MOA -->
          <div class="supporting-doc-item">
            <div class="supporting-doc-header">
              <div class="supporting-doc-info">
                <i class="las la-file-signature"></i>
                <span class="supporting-doc-label">MOA (Memorandum of Association)</span>
              </div>
              <BaseButton
                v-if="!moaDoc"
                stylevariant="primary"
                :cus_style="{ width: 'auto', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }"
                @click="uploadDocument('moa')"
                class="upload-section-btn-small"
              >
                <i class="las la-plus"></i>
                Upload
              </BaseButton>
            </div>
            <div v-if="moaDoc" class="document-card-small">
              <div class="document-card-content-small">
                <span class="document-name-small">{{ moaDoc.name }}</span>
                <span class="document-date-small">
                  <i class="las la-calendar"></i>
                  {{ formatDate(moaDoc.uploaded_at) }}
                </span>
                <span class="status-badge" :class="getStatusClass(moaDoc.status)">
                  <i :class="getStatusIcon(moaDoc.status)"></i>
                </span>
                <button 
                  v-if="moaDoc.status === 'completed' || moaDoc.status === 'success'"
                  class="view-btn"
                  @click.stop="viewDocument(moaDoc)"
                  title="View Document"
                >
                  <i class="las la-external-link-alt"></i>
                </button>
                <button 
                  class="delete-btn"
                  @click.stop="deleteDocument(moaDoc)"
                  title="Delete Document"
                >
                  <i class="las la-trash"></i>
                </button>
              </div>
            </div>
            <div v-else class="empty-document-small">
              <p>No MOA uploaded</p>
            </div>
          </div>

          <!-- AOA -->
          <div class="supporting-doc-item">
            <div class="supporting-doc-header">
              <div class="supporting-doc-info">
                <i class="las la-file-signature"></i>
                <span class="supporting-doc-label">AOA (Articles of Association)</span>
              </div>
              <BaseButton
                v-if="!aoaDoc"
                stylevariant="primary"
                :cus_style="{ width: 'auto', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }"
                @click="uploadDocument('aoa')"
                class="upload-section-btn-small"
              >
                <i class="las la-plus"></i>
                Upload
              </BaseButton>
            </div>
            <div v-if="aoaDoc" class="document-card-small">
              <div class="document-card-content-small">
                <span class="document-name-small">{{ aoaDoc.name }}</span>
                <span class="document-date-small">
                  <i class="las la-calendar"></i>
                  {{ formatDate(aoaDoc.uploaded_at) }}
                </span>
                <span class="status-badge" :class="getStatusClass(aoaDoc.status)">
                  <i :class="getStatusIcon(aoaDoc.status)"></i>
                </span>
                <button 
                  v-if="aoaDoc.status === 'completed' || aoaDoc.status === 'success'"
                  class="view-btn"
                  @click.stop="viewDocument(aoaDoc)"
                  title="View Document"
                >
                  <i class="las la-external-link-alt"></i>
                </button>
                <button 
                  class="delete-btn"
                  @click.stop="deleteDocument(aoaDoc)"
                  title="Delete Document"
                >
                  <i class="las la-trash"></i>
                </button>
              </div>
            </div>
            <div v-else class="empty-document-small">
              <p>No AOA uploaded</p>
            </div>
          </div>

          <!-- Audit Report -->
          <div class="supporting-doc-item">
            <div class="supporting-doc-header">
              <div class="supporting-doc-info">
                <i class="las la-file-invoice"></i>
                <span class="supporting-doc-label">Audit Report</span>
              </div>
              <BaseButton
                v-if="!auditReportDoc"
                stylevariant="primary"
                :cus_style="{ width: 'auto', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }"
                @click="uploadDocument('audit_report')"
                class="upload-section-btn-small"
              >
                <i class="las la-plus"></i>
                Upload
              </BaseButton>
            </div>
            <div v-if="auditReportDoc" class="document-card-small">
              <div class="document-card-content-small">
                <span class="document-name-small">{{ auditReportDoc.name }}</span>
                <span class="document-date-small">
                  <i class="las la-calendar"></i>
                  {{ formatDate(auditReportDoc.uploaded_at) }}
                </span>
                <span class="status-badge" :class="getStatusClass(auditReportDoc.status)">
                  <i :class="getStatusIcon(auditReportDoc.status)"></i>
                </span>
                <button 
                  v-if="auditReportDoc.status === 'completed' || auditReportDoc.status === 'success'"
                  class="view-btn"
                  @click.stop="viewDocument(auditReportDoc)"
                  title="View Document"
                >
                  <i class="las la-external-link-alt"></i>
                </button>
                <button 
                  class="delete-btn"
                  @click.stop="deleteDocument(auditReportDoc)"
                  title="Delete Document"
                >
                  <i class="las la-trash"></i>
                </button>
              </div>
            </div>
            <div v-else class="empty-document-small">
              <p>No Audit Report uploaded</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service';
import BaseButton from '@/components/UI/BaseButton.vue';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';

export default {
  name: 'KybPage',
  components: { BaseButton, BaseSpinner },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      kybDocuments: [],
      emiratesIdDoc: null,
      tradeLicenseDoc: null,
      creditReportDoc: null,
      moaDoc: null,
      aoaDoc: null,
      auditReportDoc: null
    };
  },
  async mounted() {
    await this.loadKybDocuments();
  },
  methods: {
    async loadKybDocuments() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await ChatbotService.getUploads(this.candidateId, {
          type: 'trade_license,emirates_id,aecb_report,moa,aoa,audit_report',
          page: 1,
          page_size: 100
        });
        
        const documents = Array.isArray(response) 
          ? response 
          : (response.data || response.files || []);
        
        const processedDocs = documents.map(doc => ({
          ...doc,
          status: doc.scan_status || doc.status || 'pending'
        }));
        
        // Group documents by type and get the most recent one for each type
        const docsByType = {};
        processedDocs.forEach(doc => {
          const type = doc.type;
          const docDate = new Date(doc.uploaded_at || doc.scan_completed_at || 0);
          
          if (!docsByType[type] || docDate > new Date(docsByType[type].uploaded_at || docsByType[type].scan_completed_at || 0)) {
            docsByType[type] = doc;
          }
        });
        
        // Assign to specific document variables
        this.emiratesIdDoc = docsByType['emirates_id'] || null;
        this.tradeLicenseDoc = docsByType['trade_license'] || null;
        this.creditReportDoc = docsByType['aecb_report'] || null;
        this.moaDoc = docsByType['moa'] || null;
        this.aoaDoc = docsByType['aoa'] || null;
        this.auditReportDoc = docsByType['audit_report'] || null;
        
        this.kybDocuments = processedDocs;
      } catch (err) {
        console.error('Error loading KYB documents:', err);
        this.error = 'Failed to load KYB documents';
      } finally {
        this.loading = false;
      }
    },
    
    uploadDocument(type) {
      // Credit Report section redirects to AECB Report upload
      const fileType = type === 'credit_report' ? 'aecb_report' : type;
      this.$router.push({
        name: 'chatbot-upload',
        params: { candidateId: this.candidateId },
        query: { type: fileType, file_type: fileType }
      });
    },
    
    viewDocument(doc) {
      const fileId = doc.id || doc.file_id;
      if (fileId) {
        this.$router.push({
          name: 'chatbot-file-details',
          params: {
            candidateId: this.candidateId,
            fileId: fileId
          }
        });
      }
    },
    
    async deleteDocument(doc) {
      const fileId = doc.id || doc.file_id;
      if (!fileId) {
        this.$bvToast.toast('Unable to delete document: File ID not found', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }
      
      // Confirm deletion
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete "${doc.name || doc.file_name || 'this document'}"? This action cannot be undone.`,
        {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      );
      
      if (!confirmed) {
        return;
      }
      
      try {
        await ChatbotService.deleteFile(fileId);
        
        // Show success message
        this.$bvToast.toast('Document deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
        
        // Reload documents to reflect the deletion
        await this.loadKybDocuments();
      } catch (error) {
        console.error('Error deleting document:', error);
        this.$bvToast.toast('Failed to delete document. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
      }
    },
    
    getDocumentIcon(type) {
      const iconMap = {
        'kyb': 'las la-file-contract',
        'trade_license': 'las la-file-contract',
        'emirates_id': 'las la-id-card',
        'credit_report': 'las la-chart-line',
        'moa': 'las la-file-signature',
        'aoa': 'las la-file-signature'
      };
      return iconMap[type] || 'las la-file';
    },
    
    formatDocumentType(type) {
      const typeMap = {
        'kyb': 'KYB Document',
        'trade_license': 'Trade License',
        'emirates_id': 'Emirates ID',
        'credit_report': 'Credit Report',
        'moa': 'MOA',
        'aoa': 'AOA'
      };
      return typeMap[type] || type || 'Document';
    },
    
    getStatusClass(status) {
      if (status === 'completed' || status === 'success') return 'status-completed';
      if (status === 'processing' || status === 'extracting') return 'status-processing';
      if (status === 'failed' || status === 'error') return 'status-error';
      return 'status-pending';
    },
    
    getStatusText(status) {
      if (status === 'completed' || status === 'success') return 'Completed';
      if (status === 'processing') return 'Processing';
      if (status === 'extracting') return 'Extracting';
      if (status === 'failed' || status === 'error') return 'Error';
      return 'Pending';
    },
    
    getStatusIcon(status) {
      if (status === 'completed' || status === 'success') return 'las la-check';
      if (status === 'processing') return 'las la-sync';
      if (status === 'extracting') return 'las la-sync';
      if (status === 'failed' || status === 'error') return 'las la-times';
      return 'las la-clock';
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../chatbot/index.scss';

.kyb-page {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.kyb-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem 2rem;
}

.loading-state, .error-state {
  text-align: center;
  padding: 3rem 2rem;
}

.error-state {
  i {
    font-size: 3rem;
    color: #dc2626;
    margin-bottom: 1rem;
  }
  
  p {
    color: #dc2626;
    margin-bottom: 1rem;
  }
}

.retry-btn {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: darken($color-primary, 10%);
    transform: translateY(-1px);
  }
}


.kyb-sections {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.kyb-section {
  background: #fbfcfe;
  border: 1px solid #eef1f6;
  border-radius: 16px;
  padding: 1.35rem 1.5rem;
}

.kyb-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.kyb-section-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.status-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;

  .status-legend-item {
    display: inline-flex;
    align-items: center;
    font-size: 0.8125rem;
    color: #4a5568;
    font-family: $brand-font-regular;

    .status-badge {
      width: 22px;
      height: 22px;
      font-size: 0.9rem;

      i {
        font-size: 1rem;
      }
    }
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: #101828;
  letter-spacing: -0.02em;
  margin: 0;
  font-family: $brand-font-bold;
  
  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 1.15rem;
    border-radius: 999px;
    background: linear-gradient(180deg, #0f9d58, #16a34a);
  }
}

.upload-section-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: auto;
  padding: 0.625rem 1rem;
  background: $color-primary;
  color: $color-secondary;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: $brand-font-medium;
  
  &:hover {
    background: darken($color-primary, 10%);
    transform: translateY(-1px);
  }
  
  i {
    font-size: 1rem;
  }
}

.upload-section-btn-small {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  width: auto;
  padding: 0.5rem 0.75rem;
  background: $color-primary;
  color: $color-secondary;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: $brand-font-medium;
  
  &:hover {
    background: darken($color-primary, 10%);
  }
  
  i {
    font-size: 0.875rem;
  }
}

.document-card {
  background: #ffffff;
  border: 1px solid #e6e9f0;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #cdd5e0;
    box-shadow: 0 8px 20px -10px rgba(16, 24, 40, 0.2);
  }
}

.document-card-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.document-icon-large {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  i {
    font-size: 1.25rem;
    color: $color-primary;
  }
}

.document-name {
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.document-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  font-family: $brand-font-regular;
  white-space: nowrap;
  flex-shrink: 0;
  
  i {
    font-size: 0.875rem;
    color: #9ca3af;
  }
}

.empty-document {
  text-align: center;
  padding: 0.9rem 0.5rem;
  background: #ffffff;
  border-radius: 12px;
  border: 1px dashed #cdd5e0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  
  i {
    font-size: 0.95rem;
    color: #98a2b3;
  }
  
  p {
    color: #667085;
    font-size: 0.875rem;
    margin: 0;
    font-family: $brand-font-medium;
  }
}

.supporting-doc-item {
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.supporting-doc-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.supporting-doc-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  i {
    font-size: 1.25rem;
    color: $color-primary;
  }
}

.supporting-doc-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.document-card-small {
  background: #ffffff;
  border: 1px solid #e6e9f0;
  border-radius: 12px;
  padding: 0.7rem 0.9rem;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #cdd5e0;
    box-shadow: 0 8px 20px -10px rgba(16, 24, 40, 0.2);
  }
}

.document-card-content-small {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.document-name-small {
  flex: 1;
  font-size: 0.95rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.document-date-small {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  font-family: $brand-font-regular;
  white-space: nowrap;
  flex-shrink: 0;
  
  i {
    font-size: 0.875rem;
    color: #9ca3af;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 1rem;
  background: transparent;
  
  &.status-completed {
    color: #16a34a;
    i { color: #16a34a; }
  }
  
  &.status-processing {
    color: #3b82f6;
    i { color: #3b82f6; }
  }
  
  &.status-error {
    color: #dc2626;
    i { color: #dc2626; }
  }
  
  &.status-pending {
    color: #6b7280;
    i { color: #6b7280; }
  }
  
  i {
    font-size: 1.3rem;
  }
}

.view-btn {
  background: transparent;
  border: none;
  color: #4a5568;
  padding: 0.1rem;
  margin-right: 0.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    i{
      color: darken(#4a5568, 10%);
    }
  }
  
  i {
    font-size: 1.3rem;
  }
}

.delete-btn {
  background: transparent;
  border: none;
  color: #dc2626;
  padding: 0.1rem;
  margin-right: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;
  
  i {
    font-size: 1.3rem;
  }

  &:hover {
    i{
      color: darken(#dc2626, 10%);
    }
  }
}

.empty-document-small {
  padding: 0.8rem 0.5rem;
  background: #ffffff;
  border-radius: 12px;
  border: 1px dashed #cdd5e0;
  text-align: center;
  
  p {
    color: #667085;
    font-size: 0.875rem;
    margin: 0;
    font-family: $brand-font-medium;
  }
}


@media (max-width: 768px) {
  .kyb-content {
    padding: 1rem;
  }
}
</style>
